import ShareContentUsageEmailModal from "@/content-usage/buttons/share/ShareContentUsageEmailModal"
import { ShareContentUsageUtils } from "@/content-usage/buttons/share/ShareContentUsageUtils"
import { ShareContentUsageByEmailButtonFragment$key } from "@/content-usage/buttons/share/__generated__/ShareContentUsageByEmailButtonFragment.graphql"
import { useContentUsageDrawerContext } from "@/content-usage/drawer/ContentUsageDrawerContext"
import { useActiveProduct } from "@/core/context/ActiveProductContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { useLabel } from "@/core/context/LabelsContext"
import PublishExperienceButton from "@/product/buttons/PublishProduct/PublishExperienceButton"
import { DiscoButton, DiscoButtonProps, DiscoLink } from "@disco-ui"
import {
  OverridableDiscoButton,
  OverridableDiscoButtonChildren,
} from "@disco-ui/button/OverridableDiscoButton"
import DiscoWarningModal from "@disco-ui/modal/DiscoWarningModal"
import useDisclosure from "@utils/hook/useDisclosure"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { graphql, useFragment } from "react-relay"

interface ShareContentUsageByEmailButtonProps extends TestIDProps, DiscoButtonProps {
  contentUsageKey: ShareContentUsageByEmailButtonFragment$key
  children: OverridableDiscoButtonChildren
}

const ShareContentUsageByEmailButton = observer(function ShareContentUsageByEmailButton({
  contentUsageKey,
  testid,
  children,
  ...props
}: ShareContentUsageByEmailButtonProps) {
  const activeProduct = useActiveProduct()
  const memberLabel = useLabel("product_member")
  const experienceDrawer = useGlobalDrawer("experienceSettings")
  const { isCurriculum } = useContentUsageDrawerContext()
  const { productSettingsUpdate } = useFeatureFlags()

  const {
    isOpen: isWarningModalOpen,
    onOpen: onWarningModalOpen,
    onClose: onWarningModalClose,
  } = useDisclosure()
  const {
    isOpen: isEmailModalOpen,
    onOpen: onEmailModalOpen,
    onClose: onEmailModalClose,
  } = useDisclosure()

  const contentUsage = useFragment<ShareContentUsageByEmailButtonFragment$key>(
    graphql`
      fragment ShareContentUsageByEmailButtonFragment on ContentUsage {
        id
        entity
        module {
          id
        }
        content {
          label
        }
      }
    `,
    contentUsageKey
  )

  const checkNoShareReason = ShareContentUsageUtils.useCheckNotShareable()
  const noShareReason = checkNoShareReason({
    usageEntity: contentUsage.entity,
    curriculumModuleId: contentUsage.module?.id,
  })

  const baseWarningProps = {
    isOpen: isWarningModalOpen,
    onClose: onWarningModalClose,
    variant: "warning" as const,
    testid: `${testid}.cannot-share-warning-modal`,
    title: "Cannot share content",
    modalContentLabel: "Cannot share content",
  }

  if (!isCurriculum) return null

  return (
    <>
      <OverridableDiscoButton onClick={handleClick} testid={testid} {...props}>
        {children}
      </OverridableDiscoButton>
      {renderNoShareModal()}
      {isEmailModalOpen && (
        <ShareContentUsageEmailModal
          isOpen={isEmailModalOpen}
          onClose={onEmailModalClose}
          contentUsageId={contentUsage.id}
          title={`Share ${contentUsage.content.label}`}
        />
      )}
    </>
  )

  function handleClick() {
    if (noShareReason) {
      onWarningModalOpen()
    } else {
      onEmailModalOpen()
    }
  }

  function handleOpenExperienceSettings() {
    onWarningModalClose()
    experienceDrawer.open({
      drawerExperienceId: activeProduct?.id,
      experienceSettingsTab: productSettingsUpdate ? "access" : "availability",
    })
  }

  function renderNoShareModal() {
    if (!isWarningModalOpen) return null

    switch (noShareReason) {
      case "draft_product":
        return (
          <DiscoWarningModal
            {...baseWarningProps}
            description={`This ${activeProduct?.typeLabel} is currently in draft mode and not visible to ${memberLabel.plural}. Publish the ${activeProduct?.typeLabel} to share this content.`}
            cancelButtonText={"Close"}
            customConfirmationButton={
              <PublishExperienceButton
                width={"100%"}
                skipConfirmation
                onPublish={onWarningModalClose}
              >
                {`Publish ${activeProduct?.typeLabel}`}
              </PublishExperienceButton>
            }
          />
        )
      case "waiting_room":
        return (
          <DiscoWarningModal
            {...baseWarningProps}
            description={
              <>
                {`${memberLabel.plural} can only see the content when the ${activeProduct?.typeLabel} starts or early access is enabled. Visit the `}
                <DiscoLink onClick={handleOpenExperienceSettings}>
                  {`${activeProduct?.typeLabel} Settings`}
                </DiscoLink>
                {` if you want to share this content.`}
              </>
            }
            cancelButtonText={null}
            customConfirmationButton={
              <DiscoButton onClick={baseWarningProps.onClose} width={"100%"}>
                {"Got it!"}
              </DiscoButton>
            }
          />
        )
      case "unreleased_module":
        return (
          <DiscoWarningModal
            {...baseWarningProps}
            description={`This content is in an unreleased module. To share, move it to a section already visible to ${memberLabel.plural} or edit this section to be released.`}
            cancelButtonText={null}
            customConfirmationButton={
              <DiscoButton onClick={baseWarningProps.onClose} width={"100%"}>
                {"Got it!"}
              </DiscoButton>
            }
          />
        )
      default:
        return null
    }
  }
})

export default ShareContentUsageByEmailButton
